import { Box, FormControl, FormHelperText, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';
import { capitalize } from 'lodash';
import { FC, useState } from 'react';
import {
  AccommodationBed,
  AccommodationBedConfiguration,
  DefaultAccommodationRoomFragment,
  DefaultAccommodationRoomRateFragment,
} from '../../graphql/generated/graphql';
import { pluralize } from '../../helpers/pluralize';
import { Iconify } from '../iconify';

export function bedLineForAccommodationBed(beds: AccommodationBed[]): string {
  return beds.map((b) => `${b.count} ${capitalize(b.name)} ${pluralize('bed', b.count)}`).join(', ');
}

export const BedLine: FC<{
  beds: AccommodationBed[];
  bedConfigurations: AccommodationBedConfiguration[] | undefined;
  bedIsGuaranteed: boolean;
  bedConfigurationId?: string | null;
  defaultToFirstBedConfiguration?: boolean;
}> = ({ beds, bedConfigurations, bedIsGuaranteed, bedConfigurationId, defaultToFirstBedConfiguration = false }) => {
  let bedElement = null;

  if (!bedConfigurations || bedConfigurations.length === 0) {
    bedElement = bedLineForAccommodationBed(beds);
  } else if (bedConfigurationId) {
    const bedConfiguration = bedConfigurations.find((bc) => bc.id === bedConfigurationId);
    if (bedConfiguration) {
      bedElement = bedLineForAccommodationBed(bedConfiguration.beds);
    }
  } else if (defaultToFirstBedConfiguration) {
    bedElement = bedLineForAccommodationBed(bedConfigurations[0].beds);
  } else {
    const bedSummaries = bedConfigurations.map((bc) => bedLineForAccommodationBed(bc.beds));

    bedElement = bedSummaries.map((summary, index) => (
      <span key={summary}>
        {index > 0 && <strong> OR </strong>}
        {summary}
      </span>
    ));
  }

  return (
    <>
      {bedElement}
      {!bedIsGuaranteed && (
        <Box component="span" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
          {' '}
          (Not guaranteed)
        </Box>
      )}
    </>
  );
};

type BedProps = {
  room: DefaultAccommodationRoomFragment;
  rate: DefaultAccommodationRoomRateFragment;
  showIcon?: boolean;
  variant?: 'body2' | 'caption';
  showGuaranteed?: boolean;
  bedConfigurationId?: string | null;
  /** After booking, if the user didn't select a bed configuration, it will default to the first bed configuration */
  defaultToFirstBedConfiguration?: boolean;
};

/** Bed line with a logo */
export const AccommodationRoomBeds: FC<BedProps> = ({
  room,
  rate,
  showIcon = true,
  variant = 'body2',
  showGuaranteed = true,
  bedConfigurationId,
  defaultToFirstBedConfiguration = false,
}) => {
  if (room.beds.length === 0 && rate.bedConfigurations?.length === 0) {
    return null;
  }

  return (
    <Typography
      variant={variant}
      sx={{
        color: 'text.primary',
      }}
    >
      {showIcon && <Iconify icon="solar:bed-linear" sx={{ mr: 1, verticalAlign: 'middle' }} />}
      <BedLine
        beds={room.beds ?? []}
        bedConfigurations={rate?.bedConfigurations}
        bedIsGuaranteed={showGuaranteed ? rate?.bedIsGuaranteed !== false : true}
        bedConfigurationId={bedConfigurationId}
        defaultToFirstBedConfiguration={defaultToFirstBedConfiguration}
      />
    </Typography>
  );
};

type AccommodationRoomBedsSelectedProps = {
  room: DefaultAccommodationRoomFragment;
  rate: DefaultAccommodationRoomRateFragment;
  callback?: (bedConfiguration: AccommodationBedConfiguration) => void;
  disabled?: boolean;
  /**
   * Meaning that if there is not more than one bed configuration, it will return null
   *
   * @default false
   */
  onlySelect?: boolean;
};

/**
 * Similar to AccommodationRoomBeds, but if there is more than one bed configuration, it will show a select dropdown to select
 * the bed configuration.
 */
export const AccommodationRoomBedsSelection: FC<AccommodationRoomBedsSelectedProps> = ({
  room,
  rate,
  callback,
  disabled = false,
  onlySelect = false,
}) => {
  const bedConfigurations = rate?.bedConfigurations;

  // A rate can come without any bed configuration (a GDS rate with no bed options), so the first one may not exist.
  const [selectedValue, setSelectedValue] = useState<string>(bedConfigurations?.[0]?.id ?? '');

  if (onlySelect && (bedConfigurations?.length ?? 0) <= 1) {
    return null;
  }

  if (!bedConfigurations || bedConfigurations.length <= 1) {
    return <AccommodationRoomBeds room={room} rate={rate} />;
  }

  const handleChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setSelectedValue(value);
    const selectedConfiguration = bedConfigurations.find((bc) => bc.id === value);

    if (selectedConfiguration) {
      callback?.(selectedConfiguration);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'top', gap: 1, mt: 1 }}>
      <Typography variant="subtitle2" sx={{ color: 'text.primary', whiteSpace: 'nowrap', pt: '5px' }}>
        Bed choice:
      </Typography>
      <FormControl size="small">
        <Select
          value={selectedValue}
          disabled={disabled}
          onChange={handleChange}
          size="small"
          sx={(theme) => ({
            '& .MuiInputBase-input': {
              fontSize: theme.typography.body2.fontSize,
              padding: '4px 12px',
            },
          })}
        >
          {bedConfigurations.map((bc) => {
            return (
              <MenuItem key={bc.id} value={bc.id} sx={{ py: '5px' }}>
                {bedLineForAccommodationBed(bc.beds)}
              </MenuItem>
            );
          })}
        </Select>
        <FormHelperText sx={{ ml: 0 }}>Bed type is not guaranteed</FormHelperText>
      </FormControl>
    </Box>
  );
};
